import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import type { Branch, Role, StaffUser, Warehouse } from '../api/types'
import { Perm } from '../api/types'
import { formatMoment } from '../ui/format'
import { suggestPassword } from '@shop/shared'
import {
  Banner,
  Button,
  ErrorBanner,
  Field,
  Panel,
  Row,
  Spinner,
  TextInput,
} from '@shop/ui'

/**
 * صفحةُ موظّف: دورُه وفروعُه ومستودعاتُه، وإعادةُ كلمته، وتعطيلُه.
 *
 * <b>وكلُّ قائمةٍ هنا تُرسَل كاملةً</b> لا «أضِف» و«احذف»: نداءان يعنيان حالةً
 * وسطى يرى فيها الموظّفُ فرعًا لا يحقّ له، أو يُحرم من فرعه بينهما. والخادمُ
 * يستقبلها هكذا عمدًا (<c>SetBranchesRequest</c>).
 */

/** مربّعُ اختيارٍ واحد — ولا مكتبةَ لأجله. */
function Check({ checked, onChange, label, hint, disabled }: {
  checked: boolean
  onChange: (value: boolean) => void
  label: string
  hint?: string
  disabled?: boolean
}) {
  return (
    <label className="flex items-start gap-2 rounded-md border border-line px-3 py-2 text-sm">
      <input
        type="checkbox"
        className="mt-1 accent-brand"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        {label}
        {hint && <span className="block text-xs text-ink-soft">{hint}</span>}
      </span>
    </label>
  )
}

export default function StaffDetail() {
  const { id = '' } = useParams()
  const queries = useQueryClient()
  const { can, session } = useAuth()

  const user = useQuery({
    queryKey: ['staff', id],
    queryFn: () => api<StaffUser>(`/api/v1/users/${id}`),
  })

  if (user.isPending) {
    return <Spinner />
  }

  if (user.isError) {
    return <ErrorBanner error={user.error} />
  }

  return (
    <Editor
      key={user.data.id}
      user={user.data}
      isSelf={session?.me.userId === user.data.id}
      can={can}
      onChanged={(fresh) => {
        queries.setQueryData(['staff', id], fresh)
        void queries.invalidateQueries({ queryKey: ['staff'] })
      }}
    />
  )
}

function Editor({ user, isSelf, can, onChanged }: {
  user: StaffUser
  isSelf: boolean
  can: (permission: string) => boolean
  onChanged: (user: StaffUser) => void
}) {
  const [error, setError] = useState<unknown>(null)
  const [done, setDone] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [resetting, setResetting] = useState(false)

  // نسخُ التحرير تُملأ مرّةً من الخادم — والمكوّنُ يُعاد بناؤه بـ`key` حين
  // يتغيّر الموظّف، فلا أثرَ يضبط حالة.
  const [roleIds, setRoleIds] = useState<string[]>([])
  const [branchIds, setBranchIds] = useState<string[]>(user.branchIds)
  const [warehouseIds, setWarehouseIds] = useState<string[]>(user.warehouseIds)
  const [rolesLoaded, setRolesLoaded] = useState(false)

  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: () => api<Role[]>('/api/v1/roles'),
    enabled: can(Perm.roleView),
  })

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => api<Branch[]>('/api/v1/branches'),
    enabled: can(Perm.branchView),
  })

  const warehouses = useQuery({
    queryKey: ['warehouses', 'all'],
    queryFn: () => api<Warehouse[]>('/api/v1/warehouses'),
    enabled: can(Perm.warehouseView),
  })

  // أدوارُ الموظّف تصل برموزها، والخادمُ يطلب معرّفاتها — فتُترجَم حين تصل
  // قائمةُ الأدوار، مرّةً واحدة.
  if (!rolesLoaded && roles.data) {
    setRolesLoaded(true)
    setRoleIds(roles.data.filter((r) => user.roleCodes.includes(r.code)).map((r) => r.id))
  }

  const save = (what: string, path: string, body: unknown) => ({
    mutationFn: () => api<StaffUser>(path, { method: 'PUT', body }),
    onSuccess: (fresh: StaffUser) => {
      setError(null)
      setDone(what)
      onChanged(fresh)
    },
    onError: (failure: unknown) => {
      setDone(null)
      setError(failure)
    },
  })

  const setRoles = useMutation(save('الأدوار', `/api/v1/users/${user.id}/roles`, { roleIds }))

  const setBranches = useMutation(save('الفروع', `/api/v1/users/${user.id}/branches`, {
    branchIds,
    defaultBranchId: branchIds[0] ?? null,
  }))

  const setWarehouses = useMutation(save('المستودعات', `/api/v1/users/${user.id}/warehouses`, {
    warehouseIds,
    defaultWarehouseId: warehouseIds[0] ?? null,
  }))

  const resetPassword = useMutation({
    mutationFn: () =>
      api<void>(`/api/v1/users/${user.id}/password`, { method: 'POST', body: { password } }),
    onSuccess: () => {
      setError(null)
      setDone('كلمةُ المرور')
      setResetting(false)
      setPassword('')
    },
    onError: setError,
  })

  const setActive = useMutation({
    mutationFn: (active: boolean) =>
      api<void>(`/api/v1/users/${user.id}/active`, {
        method: 'POST',
        body: { active, reason: active ? 'أُعيد تفعيلُه من الشاشة' : 'عُطّل من الشاشة' },
      }),
    onSuccess: () => {
      setError(null)
      setDone(user.isActive ? 'التعطيل' : 'التفعيل')
      void api<StaffUser>(`/api/v1/users/${user.id}`).then(onChanged).catch(() => undefined)
    },
    onError: setError,
  })

  const busy = setRoles.isPending || setBranches.isPending || setWarehouses.isPending
    || resetPassword.isPending || setActive.isPending

  const toggle = (list: string[], value: string) =>
    list.includes(value) ? list.filter((x) => x !== value) : [...list, value]

  return (
    <>
      <Panel title={user.fullNameAr}>
        <Row label="اسم الدخول"><span dir="ltr">{user.username}</span></Row>
        <Row label="الحالة">
          {user.isActive ? 'مفعَّل' : <span className="text-warn">معطَّل</span>}
        </Row>
        <Row label="آخرُ دخول">{formatMoment(user.lastLoginAt)}</Row>
        <Row label="كلمتُه">
          {user.mustChangePassword ? 'مؤقّتةٌ — لم يغيّرها بعد' : 'اختارها بنفسه'}
        </Row>
      </Panel>

      {isSelf && (
        <Banner tone="warn">
          <b>هذا حسابُك أنت.</b> كلُّ تغييرٍ يمسّ حقوقَك يرفع نسخةَ صلاحيّاتك،
          فيسقط رمزُك في الطلب التالي ويُخرجك النظامُ فورًا. سلوكٌ مقصود — سحبُ
          حقٍّ يسري الآن لا بعد ساعة — لكنّه يعني أنّك ستُعيد الدخول.
        </Banner>
      )}

      {done && <Banner tone="ok">حُفظ: {done}.</Banner>}
      <ErrorBanner error={error} />

      {can(Perm.userAssignRole) && (
        <Panel title="الدور">
          <div className="grid gap-2 sm:grid-cols-2">
            {(roles.data ?? []).map((role) => (
              <Check
                key={role.id}
                label={role.nameAr}
                hint={role.description ?? undefined}
                checked={roleIds.includes(role.id)}
                disabled={busy}
                onChange={() => setRoleIds((current) => toggle(current, role.id))}
              />
            ))}
          </div>

          <div className="mt-3">
            <Button tone="primary" disabled={busy} onClick={() => setRoles.mutate()}>
              حفظُ الأدوار
            </Button>
          </div>
        </Panel>
      )}

      {can(Perm.userAssignBranch) && (
        <Panel title="الفروع">
          <div className="grid gap-2 sm:grid-cols-2">
            {(branches.data ?? []).map((branch) => (
              <Check
                key={branch.id}
                label={branch.nameAr}
                checked={branchIds.includes(branch.id)}
                disabled={busy}
                onChange={() => setBranchIds((current) => toggle(current, branch.id))}
              />
            ))}
          </div>

          <p className="mt-2 text-xs text-ink-soft">
            الأوّلُ المختار هو فرعُه الافتراضيّ. <b>وبلا فرعٍ لا يرى مسوّداتٍ ولا
            يفتح واحدة.</b>
          </p>

          <div className="mt-3">
            <Button tone="primary" disabled={busy} onClick={() => setBranches.mutate()}>
              حفظُ الفروع
            </Button>
          </div>
        </Panel>
      )}

      {can(Perm.userAssignWarehouse) && (
        <Panel title="المستودعات">
          <div className="grid gap-2 sm:grid-cols-2">
            {(warehouses.data ?? []).map((warehouse) => (
              <Check
                key={warehouse.id}
                label={warehouse.nameAr}
                hint={warehouse.code}
                checked={warehouseIds.includes(warehouse.id)}
                disabled={busy}
                onChange={() => setWarehouseIds((current) => toggle(current, warehouse.id))}
              />
            ))}
          </div>

          <p className="mt-2 text-xs text-ink-soft">
            حقٌّ مستقلٌّ عن الفرع عمدًا. <b>وبلا مستودعٍ مخوَّلٍ لا يستطيع اعتمادَ
            كميّة</b> — يعتمد الصنفَ بلا رصيدٍ ولا غير.
          </p>

          <div className="mt-3">
            <Button tone="primary" disabled={busy} onClick={() => setWarehouses.mutate()}>
              حفظُ المستودعات
            </Button>
          </div>
        </Panel>
      )}

      {(can(Perm.userResetPassword) || can(Perm.userDisable)) && (
        <Panel title="إجراءاتٌ حسّاسة">
          {can(Perm.userResetPassword) && (
            <div className="mb-4">
              {resetting ? (
                <div className="space-y-3">
                  <Field
                    label="كلمةٌ مؤقّتةٌ جديدة"
                    hint="تُبطَل جلساتُه كلُّها، ويُلزَم بتغييرها عند أوّل دخول."
                  >
                    <div className="flex gap-2">
                      <TextInput value={password} onChange={setPassword} dir="ltr" disabled={busy} />
                      <Button onClick={() => setPassword(suggestPassword())} disabled={busy}>
                        اقترح
                      </Button>
                    </div>
                  </Field>

                  <div className="flex gap-2">
                    <Button
                      tone="primary"
                      disabled={busy || password.length < 10}
                      onClick={() => resetPassword.mutate()}
                    >
                      تأكيدُ إعادة التعيين
                    </Button>
                    <Button onClick={() => { setResetting(false); setPassword('') }}>تراجع</Button>
                  </div>
                </div>
              ) : (
                <Button onClick={() => setResetting(true)}>إعادةُ تعيين كلمة المرور</Button>
              )}
            </div>
          )}

          {can(Perm.userDisable) && !isSelf && (
            <div>
              <Button
                tone={user.isActive ? 'danger' : 'ghost'}
                disabled={busy}
                onClick={() => setActive.mutate(!user.isActive)}
              >
                {user.isActive ? 'تعطيلُ الحساب' : 'إعادةُ التفعيل'}
              </Button>

              <p className="mt-2 text-xs text-ink-soft">
                التعطيلُ يُخرجه من أجهزته كلِّها فورًا — ولا يُحذف الحساب:
                تاريخُه يبقى منسوبًا إليه.
              </p>
            </div>
          )}

          {can(Perm.userDisable) && isSelf && (
            <p className="text-xs text-ink-soft">
              ولا تستطيع تعطيلَ حسابك أنت — النظامُ لا يُغلق على آخرِ من يفتحه.
            </p>
          )}
        </Panel>
      )}
    </>
  )
}
